"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getPpdbList } from "@/store/controller/ppdb.controller";
import { getContactList } from "@/store/controller/contact.controller";
import { PPDB_CTA_DEFAULTS } from "./ppdbCta";

export function PPDBSection() {
  const dispatch = useAppDispatch();
  const ppdb = useAppSelector((state) => state.ppdb.list[0]);
  const contact = useAppSelector((state) => state.contact.list[0]);

  useEffect(() => {
    dispatch(getPpdbList({}));
    dispatch(getContactList({}));
  }, [dispatch]);

  if (!ppdb) return null;

  const whatsappHref = contact?.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}` : null;

  return (
    <section id="ppdb" className="py-20" style={{ background: "linear-gradient(135deg, #0d4d0d 0%, #1a7a1a 100%)" }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/30 rounded-full px-4 py-1.5 mb-5">
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
            <span className="text-yellow-300 text-sm font-semibold">Kuota Terbatas</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">{ppdb.title}</h2>
          <p className="text-2xl font-bold text-yellow-400 mb-4">{ppdb.year}</p>
          <div className="w-14 h-1 bg-yellow-400 rounded mx-auto mb-6" />
          <p className="text-green-200 max-w-xl mx-auto leading-relaxed">{ppdb.description}</p>
        </div>

        {ppdb.steps && ppdb.steps.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {ppdb.steps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center h-full hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 rounded-full bg-yellow-400 text-yellow-900 font-extrabold text-lg flex items-center justify-center mx-auto mb-4">
                    {step.step}
                  </div>
                  <h4 className="text-white font-bold mb-2">{step.title}</h4>
                  <p className="text-green-200 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {ppdb.requirements && ppdb.requirements.length > 0 && (
            <div className="bg-white/10 border border-white/20 rounded-3xl p-7">
              <h3 className="text-white font-bold text-xl mb-5">Persyaratan Pendaftaran</h3>
              <div className="space-y-3">
                {ppdb.requirements.map((requirement) => (
                  <div key={requirement} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                      <Check size={12} strokeWidth={3} className="text-yellow-900" />
                    </div>
                    <span className="text-green-100 text-sm">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center lg:text-left">
            <p className="text-green-200 text-sm mb-2">Jadwal Pendaftaran</p>
            <div className="bg-white/10 rounded-2xl p-4 mb-6 inline-block">
              <p className="text-white font-bold text-lg">{ppdb.registration}</p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
              <Link
                href="/ppdb"
                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-extrabold px-8 py-4 rounded-2xl transition-all shadow-2xl hover:shadow-yellow-400/30 text-base"
              >
                {ppdb.section_cta_label || PPDB_CTA_DEFAULTS.section}
                <ArrowRight size={20} />
              </Link>
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold px-8 py-4 rounded-2xl transition-all text-base"
                >
                  <MessageCircle size={20} />
                  Hubungi via WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
